import axios from 'axios';
import JSEncrypt from 'jsencrypt';
import _ from 'lodash';
import Common from '../constants/Common';

const textEncoder = new TextEncoder();
const textDecoder = new TextDecoder();

function getWebCrypto() {
  if (typeof window === 'undefined') return null;
  return window.crypto || window.msCrypto || null;
}

function bytesToHex(bytes) {
  return Array.from(bytes)
    .map((b) => b.toString(16).padStart(2, '0'))
    .join('');
}

function arrayBufferToBase64(buffer) {
  let binary = '';
  let bytes = new Uint8Array(buffer);
  let chunkSize = 0x8000;

  for (let i = 0; i < bytes.length; i += chunkSize) {
    let chunk = bytes.subarray(i, i + chunkSize);
    binary += String.fromCharCode.apply(null, chunk);
  }

  return btoa(binary);
}

function base64ToArrayBuffer(base64Text) {
  let binary = atob(base64Text);
  let bytes = new Uint8Array(binary.length);

  for (let i = 0; i < binary.length; i++) {
    bytes[i] = binary.charCodeAt(i);
  }

  return bytes.buffer;
}

async function deriveAesKeyString(webCrypto) {
  let secretPhraseBytes = new Uint8Array(16);
  webCrypto.getRandomValues(secretPhraseBytes);
  let secretPhraseHex = bytesToHex(secretPhraseBytes);

  let salt = new Uint8Array(16);
  webCrypto.getRandomValues(salt);

  let passphraseKey = await webCrypto.subtle.importKey(
    'raw',
    textEncoder.encode(secretPhraseHex),
    { name: 'PBKDF2' },
    false,
    ['deriveBits']
  );

  let derivedBits = await webCrypto.subtle.deriveBits(
    {
      name: 'PBKDF2',
      salt,
      iterations: 1,
      hash: 'SHA-1',
    },
    passphraseKey,
    128
  );

  return bytesToHex(new Uint8Array(derivedBits));
}

var questionOnAir = false;
function throttle(callback, limit) {
  if (!questionOnAir) {
    questionOnAir = true; // Prevent future invocations
    callback.apply(); // Execute users function
    setTimeout(function () {
      // After a period of time
      questionOnAir = false; // And allow future invocations
    }, limit);
  }
}

export function makeiv(
  length,
  characters = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789'
) {
  const webCrypto = getWebCrypto();
  var result = '';
  var charactersLength = characters.length;
  if (webCrypto?.getRandomValues) {
    let randomValues = new Uint32Array(length);
    webCrypto.getRandomValues(randomValues);
    for (var i = 0; i < length; i++) {
      result += characters.charAt(randomValues[i] % charactersLength);
    }
    return result;
  }

  for (var j = 0; j < length; j++) {
    result += characters.charAt(Math.floor(Math.random() * charactersLength));
  }
  return result;
}

let TEMP_AES_KEY_STORE = {};
async function encryption(config) {
  let pubKey = null;
  if (config.baseURL) {
    let uriSplit = config.baseURL.split('/');
    let API = uriSplit[uriSplit.length - 1];
    if (API === 'lms') {
      let moduleAPI = config.LMSModuleAPI;
      if (config.baseURL.includes(moduleAPI)) {
        pubKey = config.LMSModulePublicKey;
      } else {
        pubKey = config.LMSPublicKey;
      }
    }
  }
  if (!pubKey) return;

  let webCrypto = getWebCrypto();
  if (!webCrypto?.subtle) {
    throw new Error('Web Crypto API is not supported in this browser');
  }

  // Generate same shape as previous flow: 32-char ASCII key string.
  let aesKeyString = await deriveAesKeyString(webCrypto);

  //initialization vector - 1st 16 chars of userId
  let randomStrForIv = makeiv(16);
  let iv = textEncoder.encode(randomStrForIv);

  let aesCryptoKey = await webCrypto.subtle.importKey(
    'raw',
    textEncoder.encode(aesKeyString),
    { name: 'AES-CBC' },
    false,
    ['encrypt']
  );

  //start encryption
  let plainText = JSON.stringify(config.data);
  let encrypted = await webCrypto.subtle.encrypt(
    {
      name: 'AES-CBC',
      iv,
    },
    aesCryptoKey,
    textEncoder.encode(plainText)
  );
  let aesEncTrans = arrayBufferToBase64(encrypted);

  //encrypt iv + AES key with RSA public key
  let combineKey = randomStrForIv + aesKeyString;
  let rsaEncrypt = new JSEncrypt();
  rsaEncrypt.setPublicKey(pubKey);
  let rsaEncryptedAesKey = rsaEncrypt.encrypt(combineKey);
  //put in the information...
  config.headers['aes_key'] = rsaEncryptedAesKey;
  config.data = aesEncTrans;
  TEMP_AES_KEY_STORE[config.url] = combineKey;
}

async function decryption(response) {
  try {
    let combineKey = TEMP_AES_KEY_STORE[response.config.url];
    if (!combineKey || typeof response.data !== 'string') {
      return;
    }

    let webCrypto = getWebCrypto();
    if (!webCrypto?.subtle) {
      throw new Error('Web Crypto API is not supported in this browser');
    }

    let iv = textEncoder.encode(combineKey.substring(0, 16));
    let aesKey = textEncoder.encode(combineKey.substring(16));

    let aesCryptoKey = await webCrypto.subtle.importKey(
      'raw',
      aesKey,
      { name: 'AES-CBC' },
      false,
      ['decrypt']
    );

    let decrypted = await webCrypto.subtle.decrypt(
      {
        name: 'AES-CBC',
        iv,
      },
      aesCryptoKey,
      base64ToArrayBuffer(response.data)
    );

    let decryptedString = textDecoder.decode(decrypted);

    let aesEncTrans = JSON.parse(decryptedString);

    response.data = aesEncTrans;

    TEMP_AES_KEY_STORE = _.omit(TEMP_AES_KEY_STORE, [response.config.url]);
  } catch (error) {
    console.log('[DECRYPT FAILED]', error);
    console.log('[DECRYPT FAILED DATA]', response.data?.slice?.(0, 50));
  }
}

const Interceptor = {
  init: function () {
    axios.interceptors.request.use(
      async function (config) {
        console.log('intc-1001 [REQ] url --> ' + config.url);
        /******************************************************************/
        // Do something before request is sent

        // if (config.method === 'post' && !config.url.includes('/async/'))
        //   await encryption(config);
        /******************************************************************/
        console.log('intc-1002 [REQ]  end ok');
        return config;
      },
      function (error) {
        console.log('intc-9001 [REQ] failed');
        /******************************************************************/
        // Do something with request error

        return Promise.reject(error);
      }
    );

    // Add a response interceptor
    axios.interceptors.response.use(
      async function (response) {
        console.log('intc-2001 [RESPONSE] url --> ' + response.config.url);
        /******************************************************************/
        // Any status code that lie within the range of 2xx cause this function to trigger
        // Do something with response data

        // if (response.data && !response.config.url.includes('/async/'))
        //   await decryption(response);

        if (
          response.headers['portal-version-code'] &&
          response.headers['portal-version-code'].toString() !==
            Common.appVersion &&
          window.location.href
            .split(window.location.origin)[1]
            .split('/')[1] !== 'Customer'
        ) {
          throttle(() => {
            let question =
              'Whooa! New Version available. Refresh now to get latest?\n(This will be prompted if portal not at the latest version.)';

            if ('caches' in window) {
              caches
                .keys()
                .then((names) => {
                  return Promise.all(names.map((name) => caches.delete(name)));
                })
                .then(() => {
                  return caches.keys();
                })
                .then((namesAfterDeletion) => {
                  if (namesAfterDeletion.length === 0) {
                    console.log('All caches have been deleted.');
                  } else {
                    console.log(
                      'Some caches were not deleted:',
                      namesAfterDeletion
                    );
                  }

                  // Move the confirmation inside this .then() to ensure it's shown after cache operations
                  if (window.confirm(question)) {
                    window.location.reload(true);
                  }
                })
                .catch((error) => {
                  console.error('Error while handling caches:', error);
                });
            }
          }, 10000);
        } else if (
          response.headers['esales-portal-version-code'] &&
          response.headers['esales-portal-version-code'].toString() !==
            Common.esalesAppVersion &&
          window.location.href
            .split(window.location.origin)[1]
            .split('/')[1] === 'Customer'
        ) {
          throttle(() => {
            let question =
              'Whooa! New Version available. Refresh now to get latest?\n(This will be prompted if portal not at the latest version.)';

            if ('caches' in window) {
              caches
                .keys()
                .then((names) => {
                  return Promise.all(names.map((name) => caches.delete(name)));
                })
                .then(() => {
                  return caches.keys();
                })
                .then((namesAfterDeletion) => {
                  if (namesAfterDeletion.length === 0) {
                    console.log('All caches have been deleted.');
                  } else {
                    console.log(
                      'Some caches were not deleted:',
                      namesAfterDeletion
                    );
                  }

                  // Move the confirmation inside this .then() to ensure it's shown after cache operations
                  if (window.confirm(question)) {
                    window.location.reload(true);
                  }
                })
                .catch((error) => {
                  console.error('Error while handling caches:', error);
                });
            }
          }, 10000);
        }

        /******************************************************************/
        console.log('intc-2002 [RESPONSE] end ok');
        return response;
      },
      async function (error) {
        if (error.response) {
          console.log(
            'intc-9001 [RESPONSE] url --> ' + error.response.config.url
          );
        } else {
          console.log('intc-9001 [RESPONSE] failed');
        }
        /******************************************************************/
        // Any status codes that falls outside the range of 2xx cause this function to trigger
        // Do something with response error

        // if (error.response && error.response.data)
        //   await decryption(error.response);

        return Promise.reject(error);
      }
    );
  },
};

export default Interceptor;
